<script lang="ts">
  import { computed, inject, provide } from 'vue'
  import { useDatabaseSessionStorage } from '@gat-ship/database'
  import { useSupportSession } from '@gat-ship/support-session'
  import { useInvalidateCache } from '../../utils/invalidate-cache'
  import type { SignalRContext } from '@gat-ship/signalr'
  import {
    signalREvent,
    type PermissionsAddedToAccessRoleSignalRPayload,
    type PermissionsRemovedFromAccessRoleSignalRPayload,
    type TenantUserProfileImageChangedSignalRPayload,
    type UserRoleAssignmentAddedSignalRPayload,
    type UserRoleAssignmentRemovedSignalRPayload
  } from '@gat-ship/types/signal-r-events'
  import {
    userServiceKey,
    type UserService,
    type UserServiceContext
  } from '@gat-ship/types'
  import type { GetGrantedDatabasesResponse } from '@gat-ship/types'
  import { useUserAccessControlDataSource } from '../../utils/user-access-control'
  import { useUserProfileDataSource } from '../../utils/user-profile'
  import { useUserPreferences } from '../../utils/user-preferences'
  import { useUserStateActiveDb } from '../../utils/user-state-active-db'
  import { useUserStateGlobal } from '../../utils/user-state-global'
  import { useUserApiCalls } from '../../composables/useUserApiCalls'

  export type { UserService, UserServiceContext }

  /**
   * Creates a service provider for the user service. After the cluster
   * promotion (DL-23), this constructor no longer takes a `signalRContext`
   * parameter — `_subscribeToAccessControlEvents()` accepts it from the
   * orchestrator (`session-bootstrap.ts`) instead.
   */
  export function createUserContext() {
    const user = useUserProfileDataSource()

    const accessControl = useUserAccessControlDataSource(user.isTenantAdmin)

    const userPreferences = useUserPreferences({ userId: user.id })

    // Read-only surface adapter for database storage. Orchestration members
    // (fetchDatabases, setDatabases, addDatabase, removeDatabase, getTenantId,
    // getDefaultSupportSessionDatabaseId, getSelectedDatabaseId,
    // hasDatabaseAccess) live in the session-bootstrap orchestrator; only
    // the read accessors needed to keep `useUser().{databases, switchDatabase,
    // hasMultiDBAccess}` stable are destructured here.
    const { databaseInfo, selectDatabase, hasMultipleDatabases } =
      useDatabaseSessionStorage()

    // Use centralized support session storage. This sits BELOW
    // SupportSessionProvider in the AppContexts mount order, so the
    // `inject: false` form reads sessionStorage directly without expecting
    // the provider's context to be present yet.
    const { supportSessionId, isSupportSession: isSupportSessionFromStorage } =
      useSupportSession({
        inject: false
      })

    // Support session detection can come from user profile OR session storage
    const isSupportSession = computed(() => {
      return (
        user.profile.value?.actor !== undefined ||
        isSupportSessionFromStorage.value
      )
    })

    // Use computed database info from centralized storage
    const databases = computed(() => {
      return databaseInfo.value
    })

    /** Changes the current database and reloads the page. */
    function switchDatabase(id: string) {
      // Use centralized database selection
      const selectedDb = selectDatabase(id)
      if (selectedDb) {
        window.location.reload()
      }
    }

    const { getGrantedDatabasesRequest } = useUserApiCalls()

    /** Loads the current user's profile from the backend. */
    async function _loadUserProfile() {
      await user.fetchUserProfile()
    }

    /** Loads the current user's preferences from the backend. */
    async function _loadUserPreferences() {
      await userPreferences.fetchUserPreferences()
    }

    /**
     * Returns the granted-databases API result. The orchestrator handles
     * the storage write (`databaseStorage.setDatabases`) and the active-tenant
     * header set; this method does neither.
     */
    async function _fetchGrantedDatabases(): Promise<GetGrantedDatabasesResponse> {
      return await getGrantedDatabasesRequest()
    }

    /** Loads the current user's access codes from the backend. */
    async function _loadAccessCodes() {
      await accessControl.fetchAccessCodes()
    }

    /**
     * Subscribes to AccessControl SignalR events that affect user state
     * (permission roles, role assignments, profile image). Database-access
     * events (`DatabaseAccessGranted`/`Revoked`) live in the orchestrator
     * because their handlers mutate the database storage primitive.
     */
    function _subscribeToAccessControlEvents(signalRContext: SignalRContext) {
      signalRContext.subscribe<PermissionsAddedToAccessRoleSignalRPayload>(
        signalREvent.AccessControl.V1.PermissionsAddedToAccessRole,
        ({ message }) => {
          if (accessControl.hasRole(message.roleId)) {
            accessControl.refreshAccessCodes()
          }
        }
      )
      signalRContext.subscribe<PermissionsRemovedFromAccessRoleSignalRPayload>(
        signalREvent.AccessControl.V1.PermissionsRemovedFromAccessRole,
        ({ message }) => {
          if (accessControl.hasRole(message.roleId)) {
            accessControl.refreshAccessCodes()
          }
        }
      )
      signalRContext.subscribe<UserRoleAssignmentAddedSignalRPayload>(
        signalREvent.AccessControl.V1.UserRoleAssigned,
        ({ message }) => {
          if (message.userId === user.id.value) {
            accessControl.refreshAccessCodes()
          }
        }
      )
      signalRContext.subscribe<UserRoleAssignmentRemovedSignalRPayload>(
        signalREvent.AccessControl.V1.UserRoleUnassigned,
        ({ message }) => {
          if (message.userId === user.id.value) {
            accessControl.refreshAccessCodes()
          }
        }
      )
      signalRContext.subscribe<TenantUserProfileImageChangedSignalRPayload>(
        signalREvent.AccessControl.V1.TenantUserProfileImageChanged,
        ({ message }) => {
          if (message.userId === user.id.value) {
            user.fetchUserProfile()
          }
        }
      )
    }

    const cacheInvalidation = useInvalidateCache()
    // Initialize cooldown on service creation
    cacheInvalidation.initializeCooldown()

    const accessRoleIds = computed(() =>
      accessControl.accessroles.value.map((r) => r.id)
    )

    const userStateActiveDb = useUserStateActiveDb()
    const userStateGlobal = useUserStateGlobal()
    return {
      id: user.id,
      _loadUserProfile,
      _loadUserPreferences,
      _fetchGrantedDatabases,
      _loadAccessCodes,
      _subscribeToAccessControlEvents,
      userProfile: user.profile,
      hasPermission: accessControl.hasPermission,
      hasAgencyPermissions: accessControl.hasAgencyPermissions,
      hasMultiDBAccess: hasMultipleDatabases,
      databases,
      isTenantAdmin: user.isTenantAdmin,
      isSupportUser: user.isSupportUser,
      isSupportSession,
      supportSessionId,
      accessRoleIds,
      switchDatabase,
      cacheInvalidation,
      invalidateCache: cacheInvalidation.invalidateCache,
      isCacheCooldownActive: cacheInvalidation.isCooldownActive,
      cacheCooldownRemainingSeconds: cacheInvalidation.remainingCooldownSeconds,
      userPreferences,
      userState: {
        global: userStateGlobal,
        activeDb: userStateActiveDb
      }
    } satisfies UserService
  }

  export { userServiceKey }

  export function useUser() {
    const userService = inject(userServiceKey)

    if (!userService) {
      throw new Error(
        'could not find user-service provider. Did you provide it in your app?'
      )
    }
    return userService
  }
</script>
<script setup lang="ts">
  const userContext = createUserContext()
  provide(userServiceKey, userContext)

  defineExpose(userContext)
</script>
<template>
  <slot />
</template>
